import { reportsPosition } from '../GscTools/metrics';
import { REPORTING_TIMEZONE, formatPeriod, searchTypeLabel } from '../GscTools/period';
import { stateLabel } from './rows';
import { MAX_SELECTED } from './rule';

/**
 * The review as a file: the method, the rule and the coverage first, then one row per selected
 * query, then every page row its evaluation returned.
 *
 * The page rows are in the file, not only the candidates, because a candidate is only checkable
 * against the evidence behind it — the share column is meaningless without its denominator. Values
 * are the raw ones (shares as fractions, CTR as a fraction, position as a mean), a query that was
 * not evaluated says so in its state rather than appearing with zeroes, and only the current
 * generation is exported: a failed request never becomes a "no candidate" row.
 */
export function buildReviewCsv({ t, scope, rule, candidates, reads, counts, fetchedAt }) {
    const hasPosition = reportsPosition(scope.searchType);

    const out = [
        [t('tools.cannibalization.export.report'), t('tools.cannibalization.header')],
        [t('tools.gsc.exports.property'), scope.siteUrl],
        [t('tools.gsc.coverage.current'), formatPeriod(scope.current)],
        [t('tools.gsc.controls.search_type'), searchTypeLabel(scope.searchType, t)],
        [t('tools.gsc.controls.country'), scope.country || 'all'],
        [t('tools.gsc.controls.device'), scope.device || 'all'],
        [t('tools.gsc.exports.data_state'), t(`tools.gsc.data_state.${scope.dataState}`)],
        [t('tools.gsc.exports.timezone'), REPORTING_TIMEZONE],
        [
            t('tools.cannibalization.method.strategy'),
            t('tools.cannibalization.method.strategy_value'),
        ],
        [
            t('tools.cannibalization.method.rule'),
            t('tools.cannibalization.method.rule_value', {
                impressions: rule.minImpressions,
                urls: rule.minUrls,
                share: Math.round(rule.minShare * 100),
            }),
        ],
        [
            t('tools.cannibalization.method.denominator'),
            t('tools.cannibalization.method.denominator_value'),
        ],
        [
            t('tools.cannibalization.coverage.evaluation'),
            t('tools.cannibalization.coverage.evaluation_value', { ...counts, max: MAX_SELECTED }),
        ],
        [
            t('tools.cannibalization.coverage.history'),
            t('tools.cannibalization.coverage.history_value'),
        ],
        [t('tools.gsc.exports.fetched_at'), fetchedAt ?? ''],
        [],
        [
            t('tools.gsc.columns.query'),
            t('tools.cannibalization.candidates.retrieved_urls'),
            t('tools.cannibalization.candidates.qualifying_urls'),
            t('tools.cannibalization.candidates.page_impressions'),
            t('tools.cannibalization.candidates.largest_share'),
            t('tools.cannibalization.candidates.state'),
        ],
        ...candidates.map((row) => [
            row.key,
            row.retrievedUrls,
            row.qualifyingUrls,
            row.impressions,
            row.largestShare,
            stateLabel(t, row),
        ]),
        [],
        [
            t('tools.gsc.columns.query'),
            t('tools.cannibalization.evidence.url'),
            t('tools.gsc.metrics.clicks_short'),
            t('tools.gsc.metrics.impressions_short'),
            t('tools.cannibalization.evidence.share'),
            t('tools.cannibalization.export.qualifies'),
            t('tools.gsc.metrics.ctr_short'),
            ...(hasPosition ? [t('tools.gsc.metrics.position_short')] : []),
        ],
    ];

    for (const row of candidates) {
        for (const page of reads[row.key]?.pages ?? []) {
            out.push([
                row.key,
                page.key,
                page.clicks,
                page.impressions,
                page.share,
                page.qualifies ? 'yes' : 'no',
                page.ctr,
                ...(hasPosition ? [page.position] : []),
            ]);
        }
    }

    return out;
}
